import { useRef, useState, type MouseEvent } from "react";

import type { Id, Spot } from "../../../api/types";
import { Button } from "../../../ui";
import { locatorRect, regionFromDrag, type Size } from "../lib/locator";
import { pageImageUrl } from "../lib/paths";

interface Props {
  deliverableId: Id;
  /** The numbers of the pages that have a picture. */
  pages: readonly number[];
  /** The spots read from the page that is showing. */
  spots: readonly Spot[];
  selectedSpotId?: Id | null;
  onSelectSpot?: (id: Id) => void;
  /** Called with the page the person is on whenever it changes. */
  onPageChange?: (page: number) => void;
  /** An engineer may mark an area to add a spot by hand. */
  canMark: boolean;
  onMarked?: (page: number, region: [number, number, number, number]) => void;
  /** The dots to the inch of the picture; the default is the app's setting. */
  dpi?: number;
}

/**
 * One page of a document as a picture, with its spots drawn on it where their locators say (2.4.9). The boxes are
 * placed by shares of the picture's size, so they follow it whatever size it has on screen.
 */
export function PageViewer({ deliverableId, pages, spots, selectedSpotId, onSelectSpot, onPageChange, canMark, onMarked, dpi }: Props) {
  const [index, setIndex] = useState(0);
  const [natural, setNatural] = useState<Size | null>(null);
  const [marking, setMarking] = useState(false);
  const [drag, setDrag] = useState<{ start: { x: number; y: number }; end: { x: number; y: number } } | null>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const page = pages[Math.min(index, Math.max(pages.length - 1, 0))];

  if (page === undefined) return null;

  const go = (next: number) => {
    const clamped = Math.min(Math.max(next, 0), pages.length - 1);
    setIndex(clamped);
    setNatural(null);
    const target = pages[clamped];
    if (target !== undefined) onPageChange?.(target);
  };

  /** A mouse position as pixels of the page picture. */
  const toPicture = (event: MouseEvent): { x: number; y: number } | null => {
    const image = imageRef.current;
    if (!image || !natural) return null;
    const box = image.getBoundingClientRect();
    if (box.width === 0 || box.height === 0) return null;
    return {
      x: ((event.clientX - box.left) / box.width) * natural.width,
      y: ((event.clientY - box.top) / box.height) * natural.height,
    };
  };

  const finish = (event: MouseEvent) => {
    if (!drag || !natural) return;
    const end = toPicture(event) ?? drag.end;
    const region = regionFromDrag(drag.start, end, natural);
    setDrag(null);
    if (region) {
      setMarking(false);
      onMarked?.(page, region);
    }
  };

  const dragRect = drag && natural ? locatorRect({ region: regionFromDrag(drag.start, drag.end, natural) ?? [0, 0, 0, 0] }, natural, dpi) : null;

  return (
    <div className="page-viewer">
      <div className="page-viewer__bar row">
        <Button disabled={index === 0} onClick={() => go(index - 1)}>
          Previous page
        </Button>
        <label className="page-viewer__select">
          <span className="sr-only">Page</span>
          <select value={page} onChange={(event) => go(pages.indexOf(Number(event.target.value)))} aria-label="Page">
            {pages.map((n) => (
              <option key={n} value={n}>
                Page {n}
              </option>
            ))}
          </select>
        </label>
        <Button disabled={index >= pages.length - 1} onClick={() => go(index + 1)}>
          Next page
        </Button>
        {canMark && (
          <Button variant={marking ? "primary" : "secondary"} aria-pressed={marking} onClick={() => setMarking((m) => !m)}>
            {marking ? "Marking: drag over the area" : "Mark an area"}
          </Button>
        )}
      </div>
      <div className="page-viewer__sheet" data-marking={marking || undefined}>
        <img
          ref={imageRef}
          className="page-viewer__image"
          src={pageImageUrl(deliverableId, page)}
          alt={`Page ${page} of the document`}
          onLoad={(event) => setNatural({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight })}
          draggable={false}
        />
        {natural &&
          spots.map((spot) => {
            const rect = locatorRect(spot.locator, natural, dpi);
            if (!rect) return null;
            return (
              <button
                key={spot.id}
                type="button"
                className="spot-box"
                data-spot-id={spot.id}
                data-selected={selectedSpotId === spot.id || undefined}
                data-out={!spot.in_scope || undefined}
                style={{ left: `${rect.left}%`, top: `${rect.top}%`, width: `${rect.width}%`, height: `${rect.height}%` }}
                aria-label={`Spot: ${spot.text_read || "no text"}`}
                title={spot.text_read}
                onClick={() => onSelectSpot?.(spot.id)}
              />
            );
          })}
        {marking && (
          <div
            className="mark-layer"
            data-testid="mark-layer"
            onMouseDown={(event) => {
              const at = toPicture(event);
              if (at) setDrag({ start: at, end: at });
            }}
            onMouseMove={(event) => {
              if (!drag) return;
              const at = toPicture(event);
              if (at) setDrag({ ...drag, end: at });
            }}
            onMouseUp={finish}
          >
            {dragRect && (
              <span
                className="mark-rect"
                style={{ left: `${dragRect.left}%`, top: `${dragRect.top}%`, width: `${dragRect.width}%`, height: `${dragRect.height}%` }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
